/**
 * Team Settings Page
 * 
 * Comprehensive settings for managing a team including:
 * - General settings (name, description, color)
 * - Members management
 * - Danger zone (delete team)
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faTrash,
  faUsers,
  faCog,
  faExclamationTriangle,
  faCrown,
  faUserPlus,
  faCheck,
  faTimes,
  faPalette,
} from '@fortawesome/free-solid-svg-icons';
import { teamsAPI } from '../services/api';
import { useRBAC } from '../hooks/useRBAC';
import { Permission } from '../types/rbac';
import '../styles/TeamSettings.css';

interface TeamMember {
  id: string;
  role: string;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

interface Team {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  workspaceId: string;
  createdAt: string;
}

const TEAM_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const TeamSettings = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, workspaceRole } = useRBAC();
  
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(TEAM_COLORS[0]);
  
  // Add member form
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('MEMBER');
  const [addingMember, setAddingMember] = useState(false);
  
  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchTeam = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await teamsAPI.getById(id);
      setTeam(response.data);
      setName(response.data.name);
      setDescription(response.data.description || '');
      setColor(response.data.color || TEAM_COLORS[0]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMembers = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await teamsAPI.getMembers(id);
      setMembers(response.data?.members || response.data || []);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchTeam();
    fetchMembers();
  }, [fetchTeam, fetchMembers]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSaveGeneral = async () => {
    if (!id || !name.trim()) return;
    
    try {
      setSaving(true);
      setError(null);
      await teamsAPI.update(id, {
        name: name.trim(),
        description: description.trim() || undefined,
        color,
      });
      setSuccess('Team settings saved!');
      fetchTeam();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!id || !memberEmail.trim()) return;
    
    try {
      setAddingMember(true);
      setError(null);
      await teamsAPI.addMember(id, { email: memberEmail.trim(), role: memberRole });
      setMemberEmail('');
      setMemberRole('MEMBER');
      setSuccess('Member added!');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleChangeRole = async (memberId: string, newRole: string) => {
    if (!id) return;
    
    try {
      setError(null);
      await teamsAPI.updateMemberRole(id, memberId, newRole);
      setSuccess('Member role updated!');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;
    
    try {
      setError(null);
      await teamsAPI.removeMember(id, memberId);
      setSuccess('Member removed!');
      fetchMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleDeleteTeam = async () => {
    if (!id || deleteConfirmName !== team?.name) {
      setError('Please type the team name to confirm');
      return;
    }
    
    try {
      setDeleting(true);
      setError(null);
      await teamsAPI.delete(id);
      navigate(`/organizations/${team?.workspaceId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete team');
      setDeleting(false);
    }
  };

  const isAdmin = workspaceRole === 'OWNER' || workspaceRole === 'ORG_ADMIN';
  const canManageTeam = isAdmin || workspaceRole === 'MANAGER' || hasPermission(Permission.TEAM_EDIT);
  const canManageMembers = isAdmin || workspaceRole === 'MANAGER' || hasPermission(Permission.TEAM_MANAGE_MEMBERS);

  if (loading) {
    return (
      <div className="team-settings-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading team settings...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-settings-page">
        <div className="error-state">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <p>{error || 'Team not found'}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="team-settings-page">
      {/* Header */}
      <div className="settings-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        
        <div className="header-info">
          <div 
            className="team-icon"
            style={{ backgroundColor: team.color || TEAM_COLORS[0] }}
          >
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="header-text">
            <h1>{team.name} Settings</h1>
            <span className="team-type">Team</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{success}</span>
        </div>
      )}

      {/* Settings Layout */}
      <div className="settings-layout">
        {/* Tabs */}
        <div className="settings-tabs">
          <button 
            className={`tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>General</span>
          </button>
          
          <button 
            className={`tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            <FontAwesomeIcon icon={faUsers} />
            <span>Members</span>
          </button>
          
          {canManageTeam && (
            <button 
              className={`tab ${activeTab === 'danger' ? 'active' : ''}`}
              onClick={() => setActiveTab('danger')}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>Danger Zone</span>
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="settings-content">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2><FontAwesomeIcon icon={faCog} /> General Settings</h2>
              
              <div className="form-group">
                <label>Team Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter team name"
                  disabled={!canManageTeam}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this team..."
                  rows={3}
                  disabled={!canManageTeam}
                />
              </div>
              
              <div className="form-group">
                <label><FontAwesomeIcon icon={faPalette} /> Team Color</label>
                <div className="color-picker">
                  {TEAM_COLORS.map((c) => (
                    <button
                      key={c}
                      className={`color-option ${color === c ? 'selected' : ''}`}
                      style={{ backgroundColor: c }}
                      onClick={() => canManageTeam && setColor(c)}
                      disabled={!canManageTeam}
                    >
                      {color === c && <FontAwesomeIcon icon={faCheck} />}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="form-group readonly">
                <label>Team ID</label>
                <input type="text" value={team.id} readOnly />
              </div>
              
              <div className="form-group readonly">
                <label>Created</label>
                <input 
                  type="text" 
                  value={new Date(team.createdAt).toLocaleDateString()} 
                  readOnly 
                />
              </div>
              
              {canManageTeam && (
                <button 
                  className="btn-primary"
                  onClick={handleSaveGeneral}
                  disabled={saving || !name.trim()}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          )}

          {/* Members Section */}
          {activeTab === 'members' && (
            <div className="settings-section">
              <h2><FontAwesomeIcon icon={faUsers} /> Team Members</h2>
              
              {/* Add Member Form */}
              {canManageMembers && (
                <div className="add-member-form">
                  <h3><FontAwesomeIcon icon={faUserPlus} /> Add Member</h3>
                  <div className="add-member-row">
                    <input
                      type="email"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      placeholder="Email address"
                    />
                    <select
                      value={memberRole}
                      onChange={(e) => setMemberRole(e.target.value)}
                    >
                      <option value="MEMBER">Member</option>
                      <option value="LEAD">Lead</option>
                    </select>
                    <button
                      className="btn-primary"
                      onClick={handleAddMember}
                      disabled={addingMember || !memberEmail.trim()}
                    >
                      {addingMember ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>
              )}

              {/* Members List */}
              <div className="members-list">
                <h3>Current Members ({members.length})</h3>
                {members.length === 0 ? (
                  <p className="no-members">No members in this team yet.</p>
                ) : (
                  members.map((member) => (
                    <div key={member.id} className="member-item">
                      <div className="member-info">
                        <div className="member-avatar">
                          {member.user?.name?.charAt(0) || member.user?.email?.charAt(0) || '?'}
                        </div>
                        <div className="member-details">
                          <span className="member-name">{member.user?.name || member.user?.email}</span>
                          <span className="member-email">{member.user?.email}</span>
                        </div>
                      </div>
                      
                      <div className="member-actions">
                        {canManageMembers && member.role !== 'LEAD' ? (
                          <>
                            <select
                              value={member.role}
                              onChange={(e) => handleChangeRole(member.id, e.target.value)}
                              className="role-select"
                            >
                              <option value="MEMBER">Member</option>
                              <option value="LEAD">Lead</option>
                            </select>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRemoveMember(member.id)}
                              title="Remove member"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </>
                        ) : (
                          <span className={`role-badge ${member.role.toLowerCase()}`}>
                            {member.role === 'LEAD' && <FontAwesomeIcon icon={faCrown} />}
                            {member.role}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Danger Zone */}
          {activeTab === 'danger' && canManageTeam && (
            <div className="settings-section danger-section">
              <h2><FontAwesomeIcon icon={faExclamationTriangle} /> Danger Zone</h2>
              
              {/* Delete Team */}
              <div className="danger-action delete">
                <div className="danger-info">
                  <h3>Delete Team</h3>
                  <p>Permanently delete this team. Members will lose access to team resources.</p>
                </div>
                <button 
                  className="btn-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete Team
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Team Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header danger">
              <h3><FontAwesomeIcon icon={faExclamationTriangle} /> Delete Team</h3>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p className="danger-warning">
                This will permanently delete the team <strong>{team.name}</strong>.
              </p>
              <p>Type <strong>{team.name}</strong> to confirm:</p>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Type team name"
                className="full-width"
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteTeam}
                disabled={deleteConfirmName !== team.name || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
